import useSWR, { SWRResponse } from 'swr';
import { swrFetcher } from 'packages/lib/utils/fetch';
import { isTask, type Task } from 'packages/lib/api/annotations/contract/task';
import { patch } from 'packages/lib/utils/legacy-tsapi/rest-helpers';
import { isArrayOf } from 'packages/lib/common-types/types';
import { UnexpectedResponseType } from 'packages/lib/utils/error';
import {
  AnnotationState,
  annotationStateZod,
} from 'packages/lib/api/annotations/contract/enums';
import { keyBy, mapValues } from 'lodash';
import { patchBulk } from 'packages/lib/api/annotations/rest';

export const useTasksByTaskGroup = (
  taskGroupToken?: string
): SWRResponse<Task[], UnexpectedResponseType> =>
  useSWR<Task[]>(
    taskGroupToken
      ? `/tasks/data?taskGroupToken=${taskGroupToken}&orderBy=-createdAt`
      : null,
    (url: string) =>
      swrFetcher<Task[]>(url, {
        baseURL: 'annotations',
        typeGuard: isArrayOf(isTask),
      }),
    { revalidateOnFocus: false, suspense: false }
  );

export const assignTaskToUser = (
  taskToken: string,
  email: string | undefined
): Promise<[unknown, Response]> => {
  return patch('annotations', `/tasks/${taskToken}`, {
    assignee: email,
  });
};

const unassignAnnotationStateTarget: Partial<
  Record<AnnotationState, AnnotationState>
> = {
  QaToDo: 'QaToDo',
  QaInProgress: 'QaToDo',
  QaDone: 'QaToDo',
  AuditToDo: 'AuditToDo',
  AuditInProgress: 'AuditToDo',
  AuditDone: 'AuditToDo',
};

export const unassignTask = (
  taskToken: string,
  annotationState: AnnotationState
): Promise<[unknown, Response]> => {
  return patch('annotations', `/tasks/${taskToken}`, {
    assignee: null,
    annotationState: unassignAnnotationStateTarget[annotationState] ?? 'ToDo',
  });
};

export const resetTask = (
  taskToken: string,
  clearComments = false
): Promise<[unknown, Response]> => {
  return patch('annotations', `/tasks/${taskToken}`, {
    assignee: null,
    annotationState: annotationStateZod.enum.ToDo,
    ...(clearComments && { comments: '' }),
  });
};

export const resetManyTasks = async (tasks: Task[]): Promise<void> => {
  if (!tasks.length) {
    return;
  }

  const bulkPayload = {
    assignee: null,
    annotationState: annotationStateZod.enum.ToDo,
  };
  const manyPayloads = mapValues(keyBy(tasks, 'token'), () => bulkPayload);
  await patchBulk('/tasks/bulk', manyPayloads);
};

// Legacy method used by Audio Tool before Task Management system was finalized. Needs migration to new system.
export const changeTaskStateAPI = (
  taskToken: string,
  newState: AnnotationState
): Promise<[unknown, Response]> => {
  return patch('annotations', `/tasks/${taskToken}`, {
    annotationState: newState,
  });
};

export const changeTaskAnnotationStateAPI = (
  taskToken: string,
  newState: AnnotationState
): Promise<[unknown, Response]> => {
  return patch('annotations', `/tasks/${taskToken}`, {
    annotationState: newState,
  });
};

// Legacy method used by Audio Tool before Task Management system was finalized. Needs migration to new system.
export const rejectTaskAPI = (
  taskToken: string,
  reason: string
): Promise<[unknown, Response]> => {
  return patch('annotations', `/tasks/${taskToken}`, {
    annotationState: annotationStateZod.Enum.Error,
    comments: reason,
  });
};
